import React, { useState, useRef, useEffect, useCallback } from "react";
import "./SearchableDropdown.css";

/**
 * SearchableDropdown
 *
 * A reusable, searchable dropdown with "Load more" pagination.
 * Fits both the admin (rv-select) and public catalogue dark-luxury themes.
 *
 * Props:
 *   options           {Array}    — array of objects to list
 *   valueKey          {string}   — field name used as the value   (default: "value")
 *   labelKey          {string}   — field name used as display text (default: "label")
 *   value             {any}      — currently selected value (matches valueKey)
 *   onChange          {Function} — called with the new value when user selects
 *   placeholder       {string}   — text shown on trigger when nothing selected
 *   searchPlaceholder {string}   — placeholder inside the search input
 *   pageSize          {number}   — items shown before "Load more" (default 10)
 *   disabled          {boolean}
 *   error             {string}   — error message; adds error ring on trigger
 *   className         {string}   — extra class on the root wrapper
 *   theme             {string}   — "admin" (default) | "public"
 *   allowClear        {boolean}  — show ✕ to clear selection (default false)
 *   renderOption      {Function} — optional custom render: (option) => ReactNode
 */
export default function SearchableDropdown({
  options = [],
  valueKey = "value",
  labelKey = "label",
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  pageSize = 10,
  disabled = false,
  error,
  className = "",
  theme = "admin",
  allowClear = false,
  renderOption,
  isMulti = false,
  searchable,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  /* ── Normalize options (supports both objects and plain primitive strings/numbers) ── */
  const normalizedOptions = React.useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
      if (opt !== null && typeof opt === "object") return opt;
      return { [valueKey]: opt, [labelKey]: String(opt) };
    });
  }, [options, valueKey, labelKey]);

  /* ── Whether to display search bar ── */
  const showSearch =
    searchable === true ||
    (searchable !== false && normalizedOptions.length > 6);

  /* ── Multi vs Single selection normalization ── */
  const selectedArray = isMulti
    ? (Array.isArray(value) ? value : (value && value !== "all" ? [value] : []))
    : [];

  /* ── Derived: selected label ── */
  let selectedLabel = null;
  if (isMulti) {
    if (selectedArray.length === 0) {
      selectedLabel = null;
    } else if (selectedArray.length === 1) {
      const singleOpt = normalizedOptions.find((o) => String(o[valueKey]) === String(selectedArray[0]));
      selectedLabel = singleOpt ? singleOpt[labelKey] : String(selectedArray[0]);
    } else {
      const prefix = placeholder.includes(":") ? placeholder.split(":")[0].trim() : "COLLECTIONS";
      selectedLabel = `${prefix}: ${selectedArray.length} SELECTED`;
    }
  } else {
    const selectedOption = normalizedOptions.find((o) => String(o[valueKey]) === String(value));
    selectedLabel = selectedOption ? selectedOption[labelKey] : null;
  }

  /* ── Filtered list ── */
  const filtered = search.trim()
    ? normalizedOptions.filter((o) =>
        String(o[labelKey] ?? "")
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      )
    : normalizedOptions;

  const visible = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visibleCount;

  /* ── Open / close helpers ── */
  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setSearch("");
    setVisibleCount(pageSize);
  }, [disabled, pageSize]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    setVisibleCount(pageSize);
  }, [pageSize]);

  /* ── Auto-focus search when opened ── */
  useEffect(() => {
    if (isOpen && showSearch && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, showSearch]);

  /* ── Click-outside closes dropdown ── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, close]);

  /* ── Reset visibleCount when search changes ── */
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [search, pageSize]);

  /* ── Keyboard: Esc closes ── */
  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") close();
  };

  /* ── Select an option ── */
  const handleSelect = (option) => {
    if (isMulti) {
      const optVal = option[valueKey];
      if (optVal === "all") {
        onChange([]);
        return;
      }
      const exists = selectedArray.some((v) => String(v) === String(optVal));
      const next = exists
        ? selectedArray.filter((v) => String(v) !== String(optVal))
        : [...selectedArray, optVal];
      onChange(next);
    } else {
      onChange(option[valueKey]);
      close();
    }
  };

  /* ── Clear ── */
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(isMulti ? [] : "");
  };

  /* ── Load more ── */
  const handleLoadMore = (e) => {
    e.stopPropagation();
    setVisibleCount((c) => c + pageSize);
    if (listRef.current) {
      const prev = listRef.current.scrollTop;
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = prev;
      });
    }
  };

  const themeClass = theme === "public" ? "rv-sd--public" : "rv-sd--admin";
  const errorClass = error ? "rv-sd--error" : "";

  return (
    <div
      ref={wrapRef}
      className={`rv-sd-wrap ${themeClass} ${errorClass} ${className}`}
    >
      {/* ── Trigger button ── */}
      <button
        type="button"
        className={`rv-sd-trigger ${isOpen ? "rv-sd-trigger--open" : ""} ${!selectedLabel ? "rv-sd-trigger--placeholder" : ""}`}
        onClick={() => (isOpen ? close() : open())}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="rv-sd-trigger-label">
          {selectedLabel || placeholder}
        </span>
        <span className="rv-sd-trigger-icons">
          {(allowClear || isMulti) && (isMulti ? selectedArray.length > 0 : selectedLabel) && (
            <span
              className="rv-sd-clear-btn"
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === "Enter" && handleClear(e)}
              title="Clear selection"
            >
              <XIcon />
            </span>
          )}
          <span className={`rv-sd-chevron ${isOpen ? "rv-sd-chevron--open" : ""}`}>
            <ChevDownIcon />
          </span>
        </span>
      </button>

      {/* ── Dropdown panel ── */}
      {isOpen && (
        <div className="rv-sd-panel" role="listbox">
          {/* Search input */}
          {showSearch && (
            <div className="rv-sd-search-wrap">
              <span className="rv-sd-search-icon-wrap">
                <SearchIcon />
              </span>
              <input
                ref={searchRef}
                type="text"
                className="rv-sd-search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
              />
              {search && (
                <button
                  type="button"
                  className="rv-sd-search-clear"
                  onClick={() => setSearch("")}
                  tabIndex={-1}
                >
                  <XIcon />
                </button>
              )}
            </div>
          )}

          {/* Options list */}
          <ul ref={listRef} className="rv-sd-list">
            {visible.length === 0 ? (
              <li className="rv-sd-empty">
                <span>No results for &ldquo;{search}&rdquo;</span>
              </li>
            ) : (
              visible.map((option) => {
                const optVal = option[valueKey];
                const isActive = isMulti
                  ? (optVal === "all" ? selectedArray.length === 0 : selectedArray.some((v) => String(v) === String(optVal)))
                  : String(optVal) === String(value);
                return (
                  <li
                    key={optVal}
                    className={`rv-sd-option ${isActive ? "rv-sd-option--active" : ""}`}
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelect(option)}
                    onKeyDown={(e) => e.key === "Enter" && handleSelect(option)}
                    tabIndex={0}
                  >
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        {isMulti && optVal !== "all" && (
                          <span className={`rv-sd-checkbox ${isActive ? "rv-sd-checkbox--checked" : ""}`}>
                            {isActive && "✓"}
                          </span>
                        )}
                        <span className="rv-sd-option-label">{option[labelKey]}</span>
                        {!isMulti && isActive && (
                          <span className="rv-sd-option-check">
                            <CheckIcon />
                          </span>
                        )}
                      </>
                    )}
                  </li>
                );
              })
            )}
          </ul>

          {/* Multi-select bottom action bar */}
          {isMulti && selectedArray.length > 0 && (
            <div className="rv-sd-multi-bar">
              <span>{selectedArray.length} collection{selectedArray.length > 1 ? "s" : ""} selected</span>
              <button
                type="button"
                className="rv-sd-multi-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
              >
                Clear all
              </button>
            </div>
          )}

          {/* Load more */}
          {remaining > 0 && (
            <button
              type="button"
              className="rv-sd-load-more"
              onClick={handleLoadMore}
            >
              <ChevDownIcon />
              <span>Load {Math.min(remaining, pageSize)} more</span>
              <span className="rv-sd-load-more-count">({remaining} remaining)</span>
            </button>
          )}

          {/* Result count footer */}
          {(filtered.length > pageSize || search) && filtered.length > 0 && (
            <div className="rv-sd-footer">
              Showing {Math.min(visibleCount, filtered.length)} of {filtered.length}
              {search ? ` result${filtered.length !== 1 ? "s" : ""}` : " items"}
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && <p className="rv-sd-error-msg">{error}</p>}
    </div>
  );
}

/* ── Inline icons ── */
function ChevDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
